import {type Action, type Middleware, configureStore, isRejected} from "@reduxjs/toolkit";
import {createReduxEnhancer} from "@sentry/react";

import {type ThunkAction} from "redux-thunk";
import throttle from "lodash.throttle";
import slices from "webform/slices";

import configSlice from "webform/slices/configSlice";
import brandSlice from "slices/brandSlice";
import {getErrorNotification} from "utils/getErrorNotification";
import {notify} from "config/sentry";
import {ErrorType} from "config/errors";

const saveState = (state: ReturnType<typeof configSlice | typeof brandSlice>) => {
    const serializedState = JSON.stringify(state);
    try {
        sessionStorage.setItem("state", serializedState);
    } catch (error) {
        notify("Unable to save session state", ErrorType.SESSION_STORAGE_ACCESS_DENIED, {
            error,
        });
    }
};

export const rootReducer = slices;

export const rejectionLogger: Middleware = () => (next) => (action) => {
    if (isRejected(action)) {
        const {message, errorType, metadata} = getErrorNotification(action);
        notify(message, errorType, metadata);
    }
    return next(action);
};

const sentryReduxEnhancer = createReduxEnhancer({
    actionTransformer: (action) => ({
        type: action.type,
    }),
    stateTransformer: () => null,
});

export const store = configureStore({
    devTools: import.meta.env.DEV,
    reducer: rootReducer,
    middleware: (getDefault) => getDefault().concat(rejectionLogger),
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer),
});

store.subscribe(
    throttle(() => {
        saveState({
            locale: store.getState().config.locale,
            brand: store.getState().brand.brand,
        });
    }, 1000),
);

export type RootState = ReturnType<typeof rootReducer>;
export type AppThunk = ThunkAction<void, RootState, null, Action<string>>;
export type AppDispatch = typeof store.dispatch;
